import React, { useState } from 'react';

export default function ProductPreview() {
  const [activeTab, setActiveTab] = useState('admin');

  const previews = {
    admin: {
      role: 'Admin Overview',
      url: 'app.eduportal.school/admin',
      stats: [
        { label: 'Total Students', value: '1,280', change: '+12% this term', color: 'from-blue-500 to-indigo-500' },
        { label: 'Attendance Rate', value: '94.8%', change: 'Daily average', color: 'from-emerald-500 to-teal-500' },
        { label: 'Active Quizzes', value: '38', change: 'Auto-graded', color: 'from-purple-500 to-pink-500' },
        { label: 'Class Performance', value: '88.5%', change: 'Grade Book Avg', color: 'from-amber-500 to-orange-500' },
      ],
      recentActivity: [
        { title: 'Grade Book Updated', desc: 'Mathematics 101 midterm scores computed', time: '10m ago', icon: '📊' },
        { title: 'Attendance Logged', desc: 'Grade 10-A morning roll call submitted', time: '25m ago', icon: '✅' },
        { title: 'PDF Report Card Exported', desc: 'Term 1 progress report generated', time: '1h ago', icon: '📄' },
      ],
    },
    teacher: {
      role: 'Teacher Portal',
      url: 'app.eduportal.school/teacher',
      stats: [
        { label: 'Classes Taught', value: '5', change: 'Active sections', color: 'from-emerald-500 to-teal-500' },
        { label: 'Quizzes Created', value: '14', change: 'Auto-graded', color: 'from-blue-500 to-indigo-500' },
        { label: 'Assignments Pending', value: '8', change: 'To review', color: 'from-amber-500 to-orange-500' },
        { label: 'Attendance Recorded', value: '100%', change: 'Today', color: 'from-purple-500 to-pink-500' },
      ],
      recentActivity: [
        { title: 'Physics Quiz Published', desc: 'MCQ timer set to 30 mins', time: '5m ago', icon: '⚡' },
        { title: 'Assignment Graded', desc: '18 submissions scored & feedback sent', time: '40m ago', icon: '📝' },
        { title: 'Class Announcement', desc: 'Broadcasted to 10th Grade Physics', time: '2h ago', icon: '📢' },
      ],
    },
    student: {
      role: 'Student Workspace',
      url: 'app.eduportal.school/student',
      stats: [
        { label: 'GPA Score', value: '3.92', change: 'Top 5%', color: 'from-purple-500 to-indigo-500' },
        { label: 'Quizzes Completed', value: '22', change: 'Avg 94%', color: 'from-blue-500 to-cyan-500' },
        { label: 'Assignments Due', value: '2', change: 'This week', color: 'from-amber-500 to-orange-500' },
        { label: 'Attendance Record', value: '98%', change: 'Present', color: 'from-emerald-500 to-teal-500' },
      ],
      recentActivity: [
        { title: 'Quiz Completed', desc: 'Scored 19/20 on Chemistry MCQ', time: '15m ago', icon: '🏆' },
        { title: 'Assignment Submitted', desc: 'History Essay PDF uploaded', time: '2h ago', icon: '📤' },
        { title: 'Timetable Updated', desc: 'Lab schedule for Thursday confirmed', time: '1d ago', icon: '📅' },
      ],
    },
    parent: {
      role: 'Parent Portal',
      url: 'app.eduportal.school/parent',
      stats: [
        { label: 'Child Attendance', value: '96.4%', change: 'Excellent', color: 'from-amber-500 to-yellow-500' },
        { label: 'Latest Assessment', value: '92/100', change: 'Math Test', color: 'from-emerald-500 to-teal-500' },
        { label: 'Teacher Messages', value: '3', change: 'Unread', color: 'from-blue-500 to-indigo-500' },
        { label: 'Report Cards', value: '2 Available', change: 'PDF Export', color: 'from-purple-500 to-pink-500' },
      ],
      recentActivity: [
        { title: 'Report Card Ready', desc: 'Download Q2 Academic Summary PDF', time: '1h ago', icon: '📜' },
        { title: 'Teacher Note Received', desc: 'Mr. Davis sent a class update', time: '3h ago', icon: '💬' },
        { title: 'Attendance Alert', desc: 'Marked Present in Section A', time: '4h ago', icon: '🔔' },
      ],
    },
  };

  const current = previews[activeTab];

  return (
    <div className="relative w-full max-w-5xl mx-auto mt-12 rounded-2xl overflow-hidden border border-white/15 bg-slate-900/90 shadow-2xl shadow-blue-950/50 backdrop-blur-xl">
      {/* Browser Window Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-950/80 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>

        {/* Address Bar */}
        <div className="flex-1 max-w-sm mx-4 bg-slate-900/80 border border-white/10 rounded-lg px-3 py-1 text-xs text-slate-400 font-mono flex items-center gap-2">
          <span className="text-emerald-400">🔒</span>
          <span className="truncate">{current.url}</span>
        </div>

        {/* Live Role Selector Pills */}
        <div className="flex items-center gap-1 bg-white/5 p-1 rounded-xl border border-white/10">
          {['admin', 'teacher', 'student', 'parent'].map((key) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold capitalize transition-all ${
                activeTab === key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
              }`}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      {/* Dashboard Body Preview */}
      <div className="p-6 md:p-8 space-y-6 text-left">
        {/* Mock Top Nav Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-blue-400">EduPortal System</div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              {current.role}
              <span className="text-xs font-normal text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                Live Data Sync
              </span>
            </h3>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 bg-slate-800/60 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-300">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Academic Term 2026
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center font-bold text-xs text-white border border-white/20">
              EP
            </div>
          </div>
        </div>

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {current.stats.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white/5 border border-white/10 rounded-xl p-4 transition-all duration-300 hover:bg-white/10"
            >
              <div className="text-xs font-medium text-slate-400 mb-1">{stat.label}</div>
              <div className="text-2xl font-extrabold text-white mb-1">{stat.value}</div>
              <div className="text-[11px] font-semibold text-emerald-400 flex items-center gap-1">
                <span>↑</span> {stat.change}
              </div>
            </div>
          ))}
        </div>

        {/* Visual Charts & Feeds Split */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Visual Chart Box */}
          <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-200">Attendance & Academic Trends</span>
              <span className="text-xs text-slate-400">Weekly Analytics</span>
            </div>

            {/* Visual Bar Graph Representation */}
            <div className="h-36 flex items-end justify-between gap-3 pt-6 px-2 border-b border-white/10">
              {[65, 80, 72, 95, 88, 92, 96].map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
                  <div
                    className="w-full bg-gradient-to-t from-blue-600 to-indigo-400 rounded-t-md transition-all duration-500 group-hover:brightness-125"
                    style={{ height: `${h}%` }}
                  />
                  <span className="text-[10px] text-slate-400">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between text-xs text-slate-400">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> Attendance (94.8%)
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-indigo-400 inline-block" /> Quiz Score Avg (88.5%)
              </span>
            </div>
          </div>

          {/* Activity Feed Box */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-200">Recent Platform Updates</span>
              <span className="w-2 h-2 rounded-full bg-blue-400" />
            </div>

            <div className="space-y-3">
              {current.recentActivity.map((act, i) => (
                <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5 border border-white/5 text-xs">
                  <span className="text-base leading-none p-1 bg-white/5 rounded-md">{act.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-200 truncate">{act.title}</div>
                    <div className="text-[11px] text-slate-400 truncate">{act.desc}</div>
                  </div>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{act.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
